import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Card, Button, Spinner, PageHeader } from '../components/common';
import SignaturePad from '../components/common/SignaturePad';
import { formatDataOra } from '../utils';
import Layout from '../components/layout/Layout';
import { useWindowWidth } from '../hooks/useWindowWidth';

const semnaturaAPI = {
  getCerere:       (id)  => api.get(`/cereri/${id}`).then(r => r.data.data),
  getSemnaturi:    (id)  => api.get(`/semnaturi/cerere/${id}`).then(r => r.data.data),
  aplicaPad:       (d)   => api.post('/semnaturi/pad', d),
  getDocumentOriginal: (documentId) => api.get(`/semnaturi/document/${documentId}/original`).then(r => r.data.url),
  incarcaSemnatExtern: (cerereId, documentId, file) => {
    const form = new FormData();
    form.append('cerereId', cerereId);
    form.append('documentId', documentId);
    form.append('document', file);
    return api.post('/semnaturi/extern', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  verificaSemnatura:(id) => api.get(`/semnaturi/${id}/verifica`).then(r => r.data.data),
  getDocumente:    (id)  => api.get(`/flux/${id}`).then(r => r.data.data?.documente || []),
};

export default function SemnaturePage() {
  const { cerereId }  = useParams();
  const navigate      = useNavigate();
  const w = useWindowWidth();
  const isMobile = w < 768;
  const [tipActiv,    setTipActiv]    = useState('pad');
  const [documentId,  setDocumentId]  = useState('');
  const [showPad,     setShowPad]     = useState(false);
  const [documentHash, setDocumentHash] = useState('');
  const [fisierSemnat, setFisierSemnat] = useState(null);
  const [descarcandOriginal, setDescarcandOriginal] = useState(false);

  const { data: cerere,    isLoading: l1 } = useQuery(['cerere',    cerereId], () => semnaturaAPI.getCerere(cerereId));
  const { data: semnaturi, isLoading: l2, refetch } = useQuery(['semnaturi', cerereId], () => semnaturaAPI.getSemnaturi(cerereId));
  const { data: documente, isLoading: l3 } = useQuery(['flux-doc',  cerereId], () => semnaturaAPI.getDocumente(cerereId));

  const mutPad = useMutation(
    (d) => semnaturaAPI.aplicaPad(d),
    {
      onSuccess: (r) => {
        toast.success('Semnătură aplicată cu succes!');
        setShowPad(false);
        refetch();
        if (r.data.downloadUrl) window.open(r.data.downloadUrl, '_blank');
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Eroare aplicare semnătură'),
    }
  );

  const mutExtern = useMutation(
    ({ cerereId, documentId, file }) => semnaturaAPI.incarcaSemnatExtern(cerereId, documentId, file),
    {
      onSuccess: (r) => {
        const v = r.data.verificare;
        toast.success(
          `Semnătură verificată: ${v.semnatar?.cn || 'semnatar necunoscut'}` +
          (v.furnizorIncredere ? ` (${v.furnizorIncredere})` : '') +
          (v.calificata ? ' — calificată' : ' — avansată, fără QCStatements')
        );
        setFisierSemnat(null);
        refetch();
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Eroare la verificarea semnăturii'),
    }
  );

  async function handleDescarcaOriginal() {
    if (!documentId) { toast.warning('Selectați mai întâi un document'); return; }
    setDescarcandOriginal(true);
    try {
      const url = await semnaturaAPI.getDocumentOriginal(documentId);
      window.open(url, '_blank');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Eroare la descărcarea documentului');
    } finally {
      setDescarcandOriginal(false);
    }
  }

  function handleIncarcaSemnat() {
    if (!documentId) { toast.warning('Selectați mai întâi un document'); return; }
    if (!fisierSemnat) { toast.warning('Alegeți fișierul PDF semnat'); return; }
    mutExtern.mutate({ cerereId, documentId, file: fisierSemnat });
  }

  if (l1 || l2 || l3) return <Layout><Spinner message="Se încarcă..." /></Layout>;

  // Calculăm hash-ul documentului selectat (în practică vine de la backend)
  const documentSelectat = (documente || []).find(d => d.id === documentId);

  function handleSalvarePad(svgData, biometricData) {
    if (!documentId) { toast.warning('Selectați mai întâi un document'); return; }
    mutPad.mutate({
      cerereId,
      documentId,
      svgData,
      biometricData,
      documentHash: documentHash || 'hash-placeholder-' + Date.now(),
    });
  }


  return (
    <Layout>
      <div style={{ marginBottom: '16px' }}>
        <button onClick={() => navigate(`/cereri/${cerereId}`)} style={s.back}>← Înapoi la cerere</button>
      </div>

      <PageHeader
        title="Semnare document"
        subtitle={`Cererea ${cerere?.numar_cerere || ''}`}
      />

      <div style={{ ...s.grid2, gridTemplateColumns: isMobile ? '1fr' : '1fr 340px' }}>
        {/* Coloana stânga — semnare */}
        <div>
          {/* Selectare document */}
          <Card title="1. Selectează documentul de semnat">
            {(!documente || documente.length === 0)
              ? <p style={s.gol}>Nu există documente generate pentru această cerere. Generați un document din pagina de flux.</p>
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {documente.map(doc => (
                    <label key={doc.id} style={{
                      ...s.docOption,
                      ...(documentId === doc.id ? s.docOptionActiv : {})
                    }}>
                      <input
                        type="radio"
                        name="document"
                        value={doc.id}
                        checked={documentId === doc.id}
                        onChange={() => setDocumentId(doc.id)}
                      />
                      <div style={s.docInfo}>
                        <span style={s.docTitlu}>{doc.titlu}</span>
                        <span style={s.docMeta}>{doc.tip} · {doc.status} · {formatDataOra(doc.created_at)}</span>
                      </div>
                      {doc.status === 'semnat' && <span style={s.semnatBadge}>✓ Semnat</span>}
                    </label>
                  ))}
                </div>
              )
            }
          </Card>

          {/* Selectare tip semnătură */}
          <Card title="2. Alege tipul de semnătură">
            <div style={{ ...s.tipuri, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
              <button
                onClick={() => setTipActiv('pad')}
                style={{ ...s.tipBtn, ...(tipActiv === 'pad' ? s.tipBtnActiv : {}) }}
              >
                <span style={s.tipIcon}>🖊</span>
                <strong>Pad fizic</strong>
                <span style={s.tipDesc}>Semnătură pe tabletă Wacom la ghișeu</span>
                <span style={s.tipLegal}>Semnătură electronică simplă</span>
              </button>

              <button
                onClick={() => setTipActiv('extern')}
                style={{ ...s.tipBtn, ...(tipActiv === 'extern' ? s.tipBtnActiv : {}) }}
              >
                <span style={s.tipIcon}>🔐</span>
                <strong>Semnătură calificată</strong>
                <span style={s.tipDesc}>Orice certificat calificat (certSIGN, Trans Sped, DigiSign, AlfaTrust etc.)</span>
                <span style={s.tipLegal}>Semnătură electronică calificată</span>
              </button>
            </div>
          </Card>

          {/* Acțiune semnare */}
          <Card title="3. Aplică semnătura">
            {tipActiv === 'pad' && !showPad && (
              <div>
                <p style={s.info}>
                  Semnătura se aplică vizual pe document și include datele semnatarului,
                  data și ora, plus un hash SHA-256 pentru verificarea integrității.
                </p>
                <Button
                  onClick={() => { if (!documentId) { toast.warning('Selectați un document'); return; } setShowPad(true); }}
                  disabled={!documentId}
                >
                  🖊 Deschide pad semnătură
                </Button>
              </div>
            )}

            {tipActiv === 'pad' && showPad && (
              <SignaturePad
                width={Math.min(480, w - 48)}
                height={180}
                label="Semnați în spațiul de mai jos"
                onSave={handleSalvarePad}
                onCancel={() => setShowPad(false)}
              />
            )}

            {tipActiv === 'extern' && (
              <div>
                <p style={s.info}>
                  Descărcați documentul, semnați-l în afara aplicației cu tokenul,
                  cardul sau aplicația cloud a furnizorului dvs. (certSIGN, Trans
                  Sped, DigiSign, AlfaTrust sau orice alt furnizor acreditat —
                  format PAdES), apoi încărcați-l înapoi aici. Verificăm real
                  semnătura contra Listei de Încredere oficiale a României.
                </p>

                <div style={s.pasSemnare}>
                  <strong>Pasul 1 — descărcați documentul</strong>
                  <div style={{ marginTop: 8 }}>
                    <Button
                      onClick={handleDescarcaOriginal}
                      disabled={!documentId || descarcandOriginal}
                      variant="secondary"
                    >
                      {descarcandOriginal ? 'Se descarcă...' : '⬇ Descarcă documentul'}
                    </Button>
                  </div>
                </div>

                <div style={{ ...s.pasSemnare, marginTop: 12 }}>
                  <strong>Pasul 2 — semnați-l local, apoi încărcați-l aici</strong>
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => setFisierSemnat(e.target.files[0] || null)}
                    />
                    <Button
                      onClick={handleIncarcaSemnat}
                      disabled={!documentId || !fisierSemnat || mutExtern.isLoading}
                    >
                      {mutExtern.isLoading ? 'Se verifică...' : '🔐 Încarcă documentul semnat'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Coloana dreapta — semnături aplicate */}
        <div>
          <Card title={`Semnături aplicate (${semnaturi?.length || 0})`}>
            {!semnaturi || semnaturi.length === 0
              ? <p style={s.gol}>Nicio semnătură aplicată încă</p>
              : semnaturi.map(s2 => (
                <div key={s2.id} style={s.semnRow}>
                  <div style={s.semnIcon}>
                    {s2.tip === 'pad' ? '🖊' : '🔐'}
                  </div>
                  <div style={s.semnInfo}>
                    <span style={s.semnNume}>{s2.semnatar_nume}</span>
                    <span style={s.semnFunctie}>{s2.semnatar_functie}</span>
                    <span style={s.semnTip}>
                      {s2.tip === 'pad' ? 'Semnătură electronică simplă' : 'Semnătură electronică calificată'}
                      {s2.certificat_calificat === false && s2.tip !== 'pad' ? ' (fără QCStatements)' : ''}
                    </span>
                    {s2.certificat_cn && <span style={s.semnCert}>Cert: {s2.certificat_cn}</span>}
                    {s2.furnizor_incredere && <span style={s.semnCert}>Furnizor: {s2.furnizor_incredere}</span>}
                    <span style={s.semnData}>{formatDataOra(s2.created_at)}</span>
                  </div>
                  <div style={{ ...s.semnValid, color: s2.valid ? '#059669' : '#dc2626' }}>
                    {s2.valid ? '✓ Validă' : '✗ Invalidă'}
                  </div>
                </div>
              ))
            }
          </Card>

          {/* Informații legale */}
          <Card title="Valabilitate legală">
            <div style={s.legal}>
              <div style={s.legalItem}>
                <span style={s.legalIcon}>🖊</span>
                <div>
                  <strong style={{ fontSize: '13px' }}>Pad fizic</strong>
                  <p style={s.legalText}>Semnătură electronică simplă conform Legii 455/2001. Valabilă pentru documente interne, confirmări de primire, procese verbale.</p>
                </div>
              </div>
              <div style={s.legalItem}>
                <span style={s.legalIcon}>🔐</span>
                <div>
                  <strong style={{ fontSize: '13px' }}>Semnătură calificată (orice furnizor)</strong>
                  <p style={s.legalText}>Semnătură electronică calificată conform Regulamentului eIDAS (UE 910/2014), verificată contra Listei de Încredere oficiale a României. Valoare juridică egală cu semnătura olografă.</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

const s = {
  back:         { background: 'none', border: 'none', cursor: 'pointer', color: '#2d2d7e', fontSize: '14px', padding: 0 },
  grid2:        { display: 'grid', gridTemplateColumns: '1fr 340px', gap: '16px', alignItems: 'start' },
  gol:          { color: '#888', fontSize: '14px', textAlign: 'center', padding: '24px 0', margin: 0 },
  docOption:    { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', transition: 'all .15s' },
  docOptionActiv: { borderColor: '#2d2d7e', backgroundColor: '#eff6ff' },
  docInfo:      { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  docTitlu:     { fontSize: '14px', fontWeight: '500', color: '#1a1a2e' },
  docMeta:      { fontSize: '12px', color: '#9ca3af' },
  semnatBadge:  { fontSize: '12px', color: '#059669', fontWeight: '600' },
  tipuri:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  tipBtn:       { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '16px 12px', border: '2px solid #e5e7eb', borderRadius: '12px', cursor: 'pointer', backgroundColor: '#fff', transition: 'all .15s', textAlign: 'center' },
  tipBtnActiv:  { borderColor: '#2d2d7e', backgroundColor: '#eff6ff' },
  tipIcon:      { fontSize: '28px', marginBottom: '4px' },
  tipDesc:      { fontSize: '12px', color: '#6b7280' },
  tipLegal:     { fontSize: '10px', color: '#9ca3af', fontStyle: 'italic' },
  info:         { fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '16px' },
  pasSemnare:   { padding: '12px', backgroundColor: '#f0f4ff', borderRadius: '8px', fontSize: '13px', color: '#1a1a2e' },
  semnRow:      { display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid #f5f5f5', alignItems: 'flex-start' },
  semnIcon:     { fontSize: '24px', flexShrink: 0 },
  semnInfo:     { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  semnNume:     { fontSize: '14px', fontWeight: '600', color: '#1a1a2e' },
  semnFunctie:  { fontSize: '12px', color: '#6b7280' },
  semnTip:      { fontSize: '12px', color: '#2d2d7e', fontStyle: 'italic' },
  semnCert:     { fontSize: '11px', color: '#9ca3af' },
  semnData:     { fontSize: '11px', color: '#9ca3af' },
  semnValid:    { fontSize: '12px', fontWeight: '600', flexShrink: 0 },
  legal:        { display: 'flex', flexDirection: 'column', gap: '16px' },
  legalItem:    { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  legalIcon:    { fontSize: '24px', flexShrink: 0 },
  legalText:    { margin: '4px 0 0', fontSize: '12px', color: '#6b7280', lineHeight: '1.5' },
};
